import React, { useMemo, useState } from "react";
import moment from "moment";
import { FaArrowRight, FaArrowDown, FaArrowUp, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fmtReportRs } from "../../../utils/reportSummary";
import { isSuperAdminRole } from "../../../utils/appPaths";
import { deleteCashAdjustment } from "../../../store/Slices/CashAdjustmentSlice";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import DeleteModal from "../../../components/Modals/DeleteModal";
import { formatDateTime } from "../../../utils/dateTime";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatLoanDate = (row) => {
  const unix = Number(row?.date);
  if (Number.isFinite(unix) && unix > 0) return formatDateTime(unix);
  if (row?.f_date) return formatDateTime(row.f_date);
  return "—";
};

const buildLoanSubtitle = (row) => {
  const parts = [];
  if (row.party_name && row.party_name !== "—") parts.push(row.party_name);
  if (row.entryType === "disbursement") {
    parts.push("Loan given");
    if (row.marka) parts.push(`Marka ${row.marka}`);
    if (row.bichak_no) parts.push(`Bichak ${row.bichak_no}`);
  } else {
    parts.push("Loan repayment");
  }
  if (row.account_name && row.account_name !== "—") {
    parts.push(String(row.account_name).toUpperCase());
  }
  if (row.desc?.trim()) parts.push(row.desc.trim());
  return parts.join(" · ") || "Loan";
};

const CashAdjustmentCard = ({ row, canDelete, onDelete }) => (
  <article className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
    <div className="px-4 py-3 border-b border-line bg-subtle/80 flex items-center justify-between gap-2">
      <span className="inline-flex items-center rounded-md bg-[#155f60]/10 px-2.5 py-1 text-xs font-bold text-[#155f60]">
        {row.f_datetime || row.f_date || "—"}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-lg font-extrabold tabular-nums text-ink">
          {fmtReportRs(row.amount)}
        </span>
        {canDelete ? (
          <button
            type="button"
            title="Delete adjustment"
            onClick={() => onDelete?.(row)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-danger/30 bg-dangerSoft text-danger hover:bg-danger hover:text-white transition-colors"
          >
            <FaTrash className="text-xs" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>

    <div className="px-4 py-3 space-y-3">
      <div className="flex items-stretch gap-2">
        <div className="flex-1 min-w-0 rounded-lg bg-dangerSoft/80 border border-danger/20 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide font-bold text-danger">
            From
          </p>
          <p className="text-sm font-bold text-ink truncate">
            {row.from_account || "—"}
          </p>
          {row.from_account_no ? (
            <p className="text-[11px] text-muted">#{row.from_account_no}</p>
          ) : null}
        </div>
        <div className="flex items-center shrink-0 text-muted">
          <FaArrowRight className="text-sm" aria-hidden />
        </div>
        <div className="flex-1 min-w-0 rounded-lg bg-successSoft/80 border border-success/20 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide font-bold text-success">
            To
          </p>
          <p className="text-sm font-bold text-ink truncate">
            {row.to_account || "—"}
          </p>
          {row.to_account_no ? (
            <p className="text-[11px] text-muted">#{row.to_account_no}</p>
          ) : null}
        </div>
      </div>

      {row.description ? (
        <p className="text-sm text-inkSecondary leading-snug break-words border-t border-line pt-2">
          {row.description}
        </p>
      ) : null}
    </div>
  </article>
);

const CashLoanCard = ({ row }) => {
  const isRepayment = row.entryType === "repayment";
  const amount = Number(row.amount || 0);
  const subtitle = buildLoanSubtitle(row);

  return (
    <article className="rounded-2xl bg-card border border-line shadow-soft overflow-hidden font-quicksand">
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div
          className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
            isRepayment ? "bg-successSoft" : "bg-dangerSoft"
          }`}
        >
          {isRepayment ? (
            <FaArrowDown className="text-success text-base" aria-hidden />
          ) : (
            <FaArrowUp className="text-danger text-base" aria-hidden />
          )}
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          {amount > 0 && (
            <p
              className={`text-xl font-extrabold tabular-nums leading-none ${
                isRepayment ? "text-success" : "text-danger"
              }`}
            >
              Rs {fmtRs(amount)}
              <span
                className={`text-xs font-semibold ml-2 ${
                  isRepayment ? "text-success" : "text-danger"
                }`}
              >
                {isRepayment ? "CR" : "DR"}
              </span>
            </p>
          )}
          <p className="text-sm text-inkSecondary mt-1.5 leading-snug break-words">
            {subtitle}
          </p>
        </div>
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3 text-sm text-muted">
        <span className="shrink-0">{formatLoanDate(row)}</span>
        <span className="tabular-nums font-medium text-right truncate">
          {isRepayment ? "Cash in" : "Cash out"}
        </span>
      </div>
    </article>
  );
};

const ReportCashAdjustmentCardList = ({
  adjustments = [],
  cashLoans = [],
  cashLoanRepayments = [],
  summary = {},
  onDeleted,
}) => {
  const dispatch = useDispatch();
  const deleting = useSelector((s) => s.CashAdjustmentState?.deleting);
  const canDelete = isSuperAdminRole();
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState(null);

  const totalMoved = summary.cash_adjustment_total ?? 0;
  const showRemaining =
    (summary.cash_transferred_out ?? 0) > 0 ||
    (summary.cash_transferred_in ?? 0) > 0;

  const cashLoanRows = useMemo(() => {
    const rows = [
      ...cashLoans.map((row) => ({ ...row, entryType: "disbursement" })),
      ...cashLoanRepayments.map((row) => ({ ...row, entryType: "repayment" })),
    ];
    return rows.sort((a, b) => {
      const dateDiff = Number(a.date || 0) - Number(b.date || 0);
      if (dateDiff !== 0) return dateDiff;
      if (a.entryType !== b.entryType) {
        return a.entryType === "disbursement" ? -1 : 1;
      }
      return String(a._id).localeCompare(String(b._id));
    });
  }, [cashLoans, cashLoanRepayments]);

  const hasAdjustments = adjustments.length > 0;
  const hasLoans = cashLoanRows.length > 0;

  const handleAskDelete = (row) => {
    setSelected(row);
    setOpenDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!selected?._id) return;
    const result = await dispatch(deleteCashAdjustment(selected._id));
    if (deleteCashAdjustment.fulfilled.match(result)) {
      SuccessToast("Deleted — amount returned to source account");
      setOpenDelete(false);
      setSelected(null);
      onDeleted?.();
    } else {
      ErrorToast(result.payload || "Failed to delete adjustment");
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      {hasAdjustments && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {adjustments.map((row) => (
            <CashAdjustmentCard
              key={row._id}
              row={row}
              canDelete={canDelete}
              onDelete={handleAskDelete}
            />
          ))}
        </div>
      )}

      {hasLoans && (
        <div className="space-y-3">
          {hasAdjustments && (
            <p className="text-xs font-bold uppercase tracking-wide text-muted pt-1">
              Cash loans
            </p>
          )}
          <div className="space-y-3 rounded-2xl bg-[#F5F5F5] p-3 font-quicksand">
            {cashLoanRows.map((row) => (
              <CashLoanCard key={`${row.entryType}-${row._id}`} row={row} />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pt-1">
        <div className="rounded-xl border border-line bg-subtle px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide font-bold text-muted">
            Total transferred
          </p>
          <p className="text-xs text-muted mt-0.5">
            Internal moves — net cash unchanged
          </p>
          <p className="text-xl font-extrabold tabular-nums text-ink mt-2">
            {fmtReportRs(totalMoved)}
          </p>
        </div>

        <div className="rounded-xl border border-danger/20 bg-dangerSoft/60 px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide font-bold text-danger">
            Cash loans given
          </p>
          <p className="text-xs text-muted mt-0.5">
            {summary.cash_loan_count ?? 0} disbursement
            {(summary.cash_loan_count ?? 0) !== 1 ? "s" : ""}
          </p>
          <p className="text-xl font-extrabold tabular-nums text-danger mt-2">
            {fmtReportRs(summary.total_cash_loans_given ?? 0)}
          </p>
        </div>

        <div className="rounded-xl border border-success/20 bg-successSoft/60 px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide font-bold text-success">
            Loan repayments (cash)
          </p>
          <p className="text-xs text-muted mt-0.5">
            {summary.cash_loan_repayment_count ?? 0} repayment
            {(summary.cash_loan_repayment_count ?? 0) !== 1 ? "s" : ""}
          </p>
          <p className="text-xl font-extrabold tabular-nums text-success mt-2">
            {fmtReportRs(summary.total_cash_loan_repayments ?? 0)}
          </p>
        </div>

        {showRemaining ? (
          <div className="rounded-xl border border-success/20 bg-successSoft px-4 py-3 sm:col-span-2 xl:col-span-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-success">
              Closing cash (after transfers)
            </p>
            <p className="text-xs text-inkSecondary mt-0.5 leading-relaxed">
              Opening {fmtReportRs(summary.opening_cash ?? 0)}
              {" + "}net cash {fmtReportRs(summary.net_cash_movement)}
              {(summary.cash_transferred_out ?? 0) > 0
                ? ` − ${fmtReportRs(summary.cash_transferred_out)} out`
                : ""}
              {(summary.cash_transferred_in ?? 0) > 0
                ? ` + ${fmtReportRs(summary.cash_transferred_in)} in`
                : ""}
            </p>
            <p className="text-xl font-extrabold tabular-nums text-success mt-2">
              {fmtReportRs(
                summary.closing_cash ??
                  summary.cash_remaining_after_transfers ??
                  (summary.opening_cash ?? 0) +
                    (summary.net_cash_movement ?? 0) -
                    (summary.cash_transferred_out ?? 0) +
                    (summary.cash_transferred_in ?? 0)
              )}
            </p>
          </div>
        ) : null}
      </div>

      {openDelete ? (
        <DeleteModal
          Open={openDelete}
          setOpen={setOpenDelete}
          Loading={Boolean(deleting)}
          onSubmit={handleConfirmDelete}
        />
      ) : null}
    </div>
  );
};

export default ReportCashAdjustmentCardList;
